import React, { useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormContext, Controller } from 'react-hook-form'
import { Box, Typography, Grid } from '@mui/material'
import FormDayOfWeekCheckboxField from 'components/configured-service/accordions/ServiceDetailsAccordion/FormDayOfWeekCheckboxField'
import T from 'T'
import { HHAlert } from 'components/common/HHAlert'
import { DAYS_OF_WEEK } from '../utils/recurrenceConstants'
import { validateWeeklyRecurrence } from '../utils/validationHelpers'

const EditServiceRecurrenceDaysOfWeekSelector = ({ isCompact }) => {
  const {
    control,
    watch,
    trigger,
    formState: { errors },
  } = useFormContext()

  const watchedDaysOfWeek = watch('daysOfWeek')
  const daysOfWeek = useMemo(() => watchedDaysOfWeek || {}, [watchedDaysOfWeek])
  const recurrenceFrequency = watch('recurrenceFrequency') || '1'

  const sundaySelected = watch('daysOfWeek.sun.isSelected')
  const mondaySelected = watch('daysOfWeek.mon.isSelected')
  const tuesdaySelected = watch('daysOfWeek.tue.isSelected')
  const wednesdaySelected = watch('daysOfWeek.wed.isSelected')
  const thursdaySelected = watch('daysOfWeek.thurs.isSelected')
  const fridaySelected = watch('daysOfWeek.fri.isSelected')
  const saturdaySelected = watch('daysOfWeek.sat.isSelected')

  const requiredCount = parseInt(recurrenceFrequency, 10)
  const selectedCount = Object.entries(daysOfWeek).filter(([_, value]) => value?.isSelected).length

  const validationError = errors?.daysOfWeek?.message
  const isValid = !validationError

  // Functions/Handlers
  const handleValidationTrigger = useCallback(() => {
    if (Object.keys(daysOfWeek).length > 0 || recurrenceFrequency !== '1') {
      trigger('daysOfWeek')
    }
  }, [daysOfWeek, recurrenceFrequency, trigger])

  useEffect(() => {
    handleValidationTrigger()
  }, [
    sundaySelected,
    mondaySelected,
    tuesdaySelected,
    wednesdaySelected,
    thursdaySelected,
    fridaySelected,
    saturdaySelected,
    recurrenceFrequency,
    handleValidationTrigger,
  ])

  const validateDaysOfWeek = value => {
    const validation = validateWeeklyRecurrence(value || {}, requiredCount)
    return validation.isValid ? true : validation.message
  }

  return (
    <Box>
      <Typography variant="body1" fontWeight={500}>
        {T.ON_THESE_DAYS}
      </Typography>

      <Controller
        name="daysOfWeek"
        control={control}
        rules={{
          validate: validateDaysOfWeek,
        }}
        render={({ field }) => {
          return <input {...field} type="hidden" />
        }}
      />

      {validationError && <HHAlert severity="warning">{errors.daysOfWeek.message}</HHAlert>}

      {isValid && requiredCount === selectedCount && selectedCount > 0 && <HHAlert severity="error">{T.NEW_EVENTS_ROUTE_WARNING}</HHAlert>}

      <Grid container flexWrap={{ xs: 'wrap', sm: 'nowrap' }} mt={1} sx={{ '& > *': { flex: { xs: 1, sm: 'unset' } } }}>
        {DAYS_OF_WEEK.map(day => (
          <FormDayOfWeekCheckboxField key={day.key} day={day.key} label={day.label} isCompact={isCompact} />
        ))}
      </Grid>
    </Box>
  )
}

EditServiceRecurrenceDaysOfWeekSelector.propTypes = {
  isCompact: PropTypes.bool,
}

export default EditServiceRecurrenceDaysOfWeekSelector
